/**
 * Spot link parsing and validation for the Create New Product form.
 *
 * Shared by ProductsManager and QCTool, which each render their own copy of
 * that modal. The rule lives here so the two can't drift apart.
 *
 * Canonical stored form:
 *   https://www.abconsultingpr.com/sw1dbtables/spots/{spotId}/
 *
 * Downstream (NewProductsRequests) the spot id is recovered by taking the last
 * path segment and requiring it to be all digits, which is what makes the
 * Variety Type product lookup work. Anything that doesn't round-trip through
 * that step is worth rejecting at entry rather than discovering later.
 */

// The canonical shape, written once so the field hint and the rejection
// message can never end up describing different formats.
const SPOT_LINK_SHAPE = 'https://www.abconsultingpr.com/sw1dbtables/spots/[Spot ID]/';

// Shown as muted helper text under the field rather than as a placeholder.
// A full URL sitting inside the input reads as a value that's already there —
// same size, same position as real input, and it gets truncated on a narrow
// field. Below the input, smaller and muted, it can only read as guidance.
// Uses the [Spot ID] slot rather than a concrete id so it describes a shape
// instead of looking like a specific record someone should copy.
export const SPOT_LINK_FORMAT_HINT = `Format: ${SPOT_LINK_SHAPE}`;

// Deliberately lenient about the parts that carry no meaning, strict about the
// parts that do:
//   - scheme optional, http or https — pasting without a scheme is common
//   - `www.` optional — both forms already circulate in this codebase
//     (ProductsManager's fallback viewer opens the bare-domain form)
//   - trailing slash optional — the browser address bar drops it about as often
//     as it keeps it, and rejecting on that alone is pure friction
//   - the spot id must be digits, because that is the part we actually use
// Whatever comes in gets normalized to the canonical form above, so the value
// that reaches the DB is consistent regardless of which variant was pasted.
const SPOT_LINK_PATTERN =
  /^(?:https?:\/\/)?(?:www\.)?abconsultingpr\.com\/sw1dbtables\/spots\/(\d+)\/?$/i;

/**
 * Canonical SW1 link for a spot id.
 *
 * @param {number|string} spotId
 * @returns {string|null} null when the id isn't a positive integer
 */
export function buildSpotUrl(spotId) {
  const id = String(spotId ?? '').trim();
  if (!/^\d+$/.test(id) || Number(id) <= 0) return null;
  return `https://www.abconsultingpr.com/sw1dbtables/spots/${id}/`;
}

/**
 * Validate a pasted spot link.
 *
 * @param {string} value - Raw field value.
 * @param {Object} [options]
 * @param {boolean} [options.required=false] - Treat empty as invalid.
 * @returns {{valid: boolean, spotId: string, normalized: string, error: string|null}}
 *   `normalized` is the canonical link to store (empty when the value is empty
 *   and not required); `error` is a user-facing message, or null when valid.
 */
export function validateSpotLink(value, { required = false } = {}) {
  const trimmedValue = String(value ?? '').trim();

  if (!trimmedValue) {
    return {
      valid: !required,
      spotId: '',
      normalized: '',
      error: required ? 'Spot Link is required.' : null,
    };
  }

  const match = trimmedValue.match(SPOT_LINK_PATTERN);

  if (!match) {
    return {
      valid: false,
      spotId: '',
      normalized: '',
      error: `That isn't a spot link. Use ${SPOT_LINK_SHAPE}`,
    };
  }

  const spotId = match[1];

  return {
    valid: true,
    spotId,
    normalized: `https://www.abconsultingpr.com/sw1dbtables/spots/${spotId}/`,
    error: null,
  };
}
